import { useState, type ReactNode } from 'react';
import { Button } from '@paige/ui';
import './insights.css';

/** One write in the Actions menu: enabled with its handler, or shown disabled with who or what enables it. */
export interface AccountAction {
  key: string;
  label: string;
  hint: string;
  onSelect: (() => void) | null;
  /** Why it is not offered to this role, or what step brings it; shown in place of the hint. */
  disabledWhy?: string;
}

export interface AccountActionsProps {
  /** "View as customer": the app's support-grant door, read-only unless the role may act. */
  viewAsCustomer: ReactNode;
  /** The account summary as plain text, for the clipboard. */
  summary: string;
  onNote: (kind: 'note' | 'next_step') => void;
  canNote: boolean;
  /** Every write the menu knows, for every role; a disabled one says why (round 2, support R2-M5). */
  actions: readonly AccountAction[];
}

function MenuItem({ a, close }: { a: AccountAction; close: () => void }): ReactNode {
  return (
    <li role="none">
      <button
        role="menuitem"
        type="button"
        disabled={!a.onSelect}
        aria-disabled={!a.onSelect}
        onClick={() => {
          close();
          a.onSelect?.();
        }}
      >
        {a.label}
        <span className="ad-small">{a.onSelect ? a.hint : (a.disabledWhy ?? a.hint)}</span>
      </button>
    </li>
  );
}

/** The menu of writes that change the account: each opens a dialog with a reason and a preview. */
function ActionsMenu({ actions }: { actions: readonly AccountAction[] }): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <div className="ad-menu">
      <Button aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        Actions <span aria-hidden="true">▾</span>
      </Button>
      {open ? (
        <ul
          className="ad-menu__list"
          role="menu"
          onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        >
          {actions.map((a) => (
            <MenuItem key={a.key} a={a} close={() => setOpen(false)} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * The customer page's header actions (round 2): one primary door, "View as customer"; the sales
 * actions (copy the summary, a note, the next step); and every account-changing write behind one
 * menu, the ones this role cannot make shown disabled with the role or step that brings them.
 */
export function AccountActions(props: AccountActionsProps): ReactNode {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(props.summary).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <>
      {props.viewAsCustomer}
      <Button onClick={copy}>{copied ? 'Copied' : 'Copy summary'}</Button>
      {props.canNote ? <Button onClick={() => props.onNote('note')}>Add note</Button> : null}
      {props.canNote ? <Button onClick={() => props.onNote('next_step')}>Next step</Button> : null}
      {props.actions.length ? <ActionsMenu actions={props.actions} /> : null}
    </>
  );
}
